import {
  useCallback,
  useEffect,
  useId,
  useState,
  type FormEvent,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../app/providers/AuthProvider";
import { AuthProviderButtons } from "../auth/AuthProviderButtons";
import { ApiError } from "../../lib/api-client";
import { getFamilyByInviteCode, joinFamily } from "../../lib/api-family";
import type { FamilyPreview } from "../../types/family";

/** sessionStorage key used to resume the join flow after authentication. */
export const PENDING_INVITE_KEY = "fg_pending_invite_code";

const MAX_NICKNAME_LEN = 255;

const INVALID_LINK_TITLE = "Ссылка недействительна";
const INVALID_LINK_BODY =
  "Ссылка недействительна или устарела. Возможно, администратор обновил код приглашения.";
const ALREADY_TITLE = "Вы уже состоите в семье";
const ALREADY_BODY =
  "В текущей версии приложения каждый пользователь может состоять только в одной семье.";
const GENERIC_ERROR = "Что-то пошло не так. Попробуйте ещё раз.";

type ViewState =
  | { kind: "loading" }
  | { kind: "form"; family: FamilyPreview }
  | { kind: "invalid" }
  | { kind: "already"; familyId: string | null }
  | { kind: "error"; message: string };

function Header() {
  return (
    <header className="flex items-center justify-center px-4 py-5">
      <h1 className="text-lg font-extrabold tracking-[-0.03em] text-content">Присоединиться</h1>
    </header>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden="true"
      className="block h-12 w-12 animate-spin rounded-full border-[3px] border-action border-t-transparent"
    />
  );
}

export function JoinFamily() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const nicknameId = useId();
  const helperId = useId();
  const formErrorId = useId();

  const code = searchParams.get("code") ?? "";

  const [view, setView] = useState<ViewState>({ kind: "loading" });
  const [nickname, setNickname] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const loadFamily = useCallback(async () => {
    if (!code.trim()) {
      sessionStorage.removeItem(PENDING_INVITE_KEY);
      setView({ kind: "invalid" });
      return;
    }
    setView({ kind: "loading" });
    try {
      const family = await getFamilyByInviteCode(code);
      setView({ kind: "form", family });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        sessionStorage.removeItem(PENDING_INVITE_KEY);
        setView({ kind: "invalid" });
      } else {
        setView({ kind: "error", message: GENERIC_ERROR });
      }
    }
  }, [code]);

  useEffect(() => {
    void loadFamily();
  }, [loadFamily]);

  function preserveInviteForAuth() {
    sessionStorage.setItem(PENDING_INVITE_KEY, code);
  }

  const trimmedNickname = nickname.trim();
  const canSubmit =
    trimmedNickname.length > 0 &&
    trimmedNickname.length <= MAX_NICKNAME_LEN &&
    !isSubmitting;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit) return;

    setIsSubmitting(true);
    setFormError(null);

    try {
      const family = await joinFamily(code, trimmedNickname);
      sessionStorage.removeItem(PENDING_INVITE_KEY);
      navigate(`/families/${family.id}`, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === "INVALID_INVITE_CODE") {
        setView({ kind: "invalid" });
      } else if (err instanceof ApiError && err.code === "ALREADY_IN_FAMILY") {
        setView({ kind: "already", familyId: null });
      } else if (err instanceof ApiError && err.code === "VALIDATION_FAILED") {
        setFormError("Проверьте имя: 1–255 символов.");
        setIsSubmitting(false);
      } else {
        setFormError(GENERIC_ERROR);
        setIsSubmitting(false);
      }
    }
  }

  // Wait for the auth provider to finish restoring the session before deciding
  // whether to show the form or the login prompt.
  if (view.kind === "loading" || authLoading) {
    return (
      <div className="flex min-h-screen flex-col bg-canvas">
        <Header />
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6">
          <Spinner />
          <p role="status" aria-live="polite" className="text-sm text-muted">
            Загрузка данных семьи…
          </p>
        </div>
      </div>
    );
  }

  if (view.kind === "invalid") {
    return (
      <div className="flex min-h-screen flex-col bg-canvas">
        <Header />
        <main className="flex flex-1 flex-col items-center px-6 pt-16 text-center">
          <div
            aria-hidden="true"
            className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-[#FEE2E2] text-3xl font-bold text-[#EF4444]"
          >
            !
          </div>
          <h2 className="mb-2 text-xl font-semibold text-content">
            {INVALID_LINK_TITLE}
          </h2>
          <p className="mb-6 max-w-[300px] text-sm text-muted">
            {INVALID_LINK_BODY}
          </p>
          <div
            role="alert"
            className="mb-6 w-full rounded-lg border border-[#FCA5A5] bg-danger-soft px-4 py-3 text-sm font-medium text-danger"
          >
            INVALID_INVITE_CODE: Ссылка недействительна
          </div>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="h-[50px] w-full rounded-xl bg-action text-base font-semibold text-white transition-colors hover:bg-action-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-action focus-visible:ring-offset-2 focus-visible:ring-offset-canvas cursor-pointer"
          >
            На главную
          </button>
        </main>
      </div>
    );
  }

  if (view.kind === "already") {
    return (
      <div className="flex min-h-screen flex-col bg-canvas">
        <Header />
        <main className="flex flex-1 flex-col items-center px-6 pt-16 text-center">
          <div
            aria-hidden="true"
            className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-[#DBEAFE] text-3xl font-bold text-[#2563EB]"
          >
            i
          </div>
          <h2 className="mb-2 text-xl font-semibold text-content">
            {ALREADY_TITLE}
          </h2>
          <p className="mb-6 max-w-[300px] text-sm text-muted">
            {ALREADY_BODY}
          </p>
          <div
            role="alert"
            className="mb-6 w-full rounded-lg border border-[#93C5FD] bg-[#DBEAFE] px-4 py-3 text-sm font-medium text-[#1D4ED8]"
          >
            ALREADY_IN_FAMILY: Вы уже состоите в семье
          </div>
          <button
            type="button"
            onClick={() =>
              navigate(view.familyId ? `/families/${view.familyId}` : "/")
            }
            className="h-[50px] w-full rounded-xl bg-action text-base font-semibold text-white transition-colors hover:bg-action-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-action focus-visible:ring-offset-2 focus-visible:ring-offset-canvas cursor-pointer"
          >
            Перейти к моей семье
          </button>
        </main>
      </div>
    );
  }

  if (view.kind === "error") {
    return (
      <div className="flex min-h-screen flex-col bg-canvas">
        <Header />
        <main className="flex flex-1 flex-col items-center px-6 pt-16 text-center">
          <h2 className="mb-2 text-xl font-semibold text-content">
            Не удалось загрузить
          </h2>
          <p className="mb-6 max-w-[300px] text-sm text-muted">
            {view.message}
          </p>
          <button
            type="button"
            onClick={() => void loadFamily()}
            className="h-[50px] w-full rounded-xl bg-action text-base font-semibold text-white transition-colors hover:bg-action-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-action focus-visible:ring-offset-2 focus-visible:ring-offset-canvas cursor-pointer"
          >
            Повторить
          </button>
        </main>
      </div>
    );
  }

  // view.kind === "form"
  const { family } = view;

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-2xl flex-col bg-canvas md:min-h-full lg:justify-center lg:py-8">
      <Header />

      <main className="flex flex-1 flex-col px-6 pt-6">
        <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-action">Вас приглашают</p>
        <h2 className="mb-2 text-[28px] font-extrabold tracking-[-0.05em] text-content">{family.name}</h2>
        <p className="mb-6 text-sm text-muted">
          {isAuthenticated
            ? "Вас пригласили присоединиться к семье. Введите ваше имя в семье."
            : "Вас пригласили присоединиться к семье. Сначала выберите способ входа."}
        </p>

        {!isAuthenticated ? (
          <section aria-labelledby="invite-auth-title">
            <h3
              id="invite-auth-title"
              className="mb-4 text-lg font-bold text-content"
            >
              Войдите, чтобы присоединиться
            </h3>
            <AuthProviderButtons onProviderSelect={preserveInviteForAuth} />
          </section>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <label
              htmlFor={nicknameId}
              className="mb-2 block text-sm font-semibold text-content"
            >
              Ваше имя в семье
            </label>
            <input
              id={nicknameId}
              type="text"
              value={nickname}
              onChange={(event) => {
                setNickname(event.target.value);
                if (formError) setFormError(null);
              }}
              maxLength={MAX_NICKNAME_LEN}
              placeholder="Папа"
              autoComplete="off"
              required
              disabled={isSubmitting}
              aria-invalid={formError !== null}
              aria-describedby={
                formError ? `${formErrorId} ${helperId}` : helperId
              }
              className="form-control h-[50px] w-full rounded-xl border border-action bg-white px-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-action/40 disabled:cursor-not-allowed"
            />

            <p id={helperId} className="mt-2 text-xs text-muted">
              1–255 символов
            </p>

            {formError && (
              <div
                id={formErrorId}
                role="alert"
                className="mt-3 rounded-lg border-l-4 border-danger bg-danger-soft px-4 py-3 text-sm font-medium text-danger"
              >
                {formError}
              </div>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              className={`mt-6 h-[50px] w-full rounded-xl text-base font-semibold text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas ${
                canSubmit
                  ? "bg-action hover:bg-action-hover focus-visible:ring-action cursor-pointer"
                  : "cursor-not-allowed bg-surface-disabled"
              }`}
            >
              {isSubmitting ? "Присоединяемся…" : "Присоединиться"}
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
